import React, { MouseEvent } from 'react';

import NextImage from 'next/image';

import { useMap } from '../../../contexts/MapContext';
import { useMapLayers } from '../../../contexts/MapLayersContext';
import { EMPTY_LIST_SIX } from '../../../helpers/emptyListsForMapping';
import { IFishingWater } from '../../../interfaces/rutilus/fishingWaters';
import { IMapContextSearchResult } from '../../../interfaces/search';
import { useExplore } from '../../../views/Explore/context/ExploreContext';
import { getExploreUrl } from '../../../views/Explore/context/helpers';
import { NonCrawlableLink } from '../../Clickables/NonCrawlableLink/NonCrawlableLink';
import { Text } from '../../Text/Text';
import locationIcon from '../assets/location.svg';
import waterIcon from '../assets/water.svg';
import { activateFishingWaterLayerIfNeeded } from '../helpers/activateFishingWaterLayerIfNeeded';
import {
  MAP_EASE_MAX,
  MAP_EASE_MIN,
  MAX_NUMBER_OF_LOCATIONS_IN_RESULT,
} from '../helpers/constants';

interface IOptionsProps {
  results?: IMapContextSearchResult;
  isFetching: boolean;
  setIsActive: (isActive: boolean) => void;
  trackSelection: (name: string, type: 'fishing_water' | 'places', waterId?: string) => void;
}

export const SearchOptionsMapContext = ({
  results,
  isFetching,
  setIsActive,
  trackSelection,
}: IOptionsProps) => {
  const {
    layers: {
      fishingWaters: { isWholeLayerVisible, toggleableGroups },
    },
    setNewLayerConfig,
  } = useMapLayers();
  const { saveParamsInUrl } = useExplore();
  const { map } = useMap();

  if (!results && !isFetching) {
    return <></>;
  }

  const locationList = results?.locationList || [];

  const getZoomLevel = (): number => {
    if (map) {
      const zoomLevel = map.getZoom();
      if (zoomLevel > MAP_EASE_MAX) {
        map.setZoom(MAP_EASE_MAX);
        return MAP_EASE_MAX;
      }
      if (zoomLevel < MAP_EASE_MIN) {
        map.setZoom(MAP_EASE_MIN);
        return MAP_EASE_MIN;
      }
    }
    return MAP_EASE_MAX;
  };

  const easeMap = ({ latitude, longitude }: { latitude: number; longitude: number }): void => {
    if (map) {
      const zoomLevel = getZoomLevel();
      map.setZoom(zoomLevel);
      map.easeTo({ center: { lat: latitude, lon: longitude } });
    }
  };

  const handleOnWaterClick =
    ({ external_id, name, latitude, longitude, catches_counter }: IFishingWater['fishing_water']) =>
    () => {
      activateFishingWaterLayerIfNeeded({
        hasCatches: catches_counter !== 0,
        isWholeLayerVisible,
        toggleableGroups,
        setNewLayerConfig,
      });
      setIsActive(false);

      const zoomLevel = getZoomLevel();

      saveParamsInUrl({
        drawerViewId: external_id,
        drawerView: 'fishing-water',
        longitude,
        latitude,
        search: name,
        zoom: zoomLevel,
      });

      easeMap({ latitude, longitude });

      trackSelection(name, 'fishing_water', external_id);
    };

  const handleOnLocationClick =
    ({ name, latitude, longitude }: { name: string; latitude: number; longitude: number }) =>
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      setIsActive(false);

      const zoomLevel = getZoomLevel();

      saveParamsInUrl({ search: name, longitude, latitude, zoom: zoomLevel });

      easeMap({ latitude, longitude });
      trackSelection(name, 'places');
    };

  return (
    <div data-testid="search-options" className="md:border-twilight py-2 md:border-t-2">
      {isFetching &&
        EMPTY_LIST_SIX.map(({ id }) => (
          <div className="flex animate-pulse px-6 py-2 font-normal" key={id}>
            <div className="bg-twilight size-10 flex-none rounded-sm" />
            <div className="ml-4 w-full space-y-3 overflow-hidden">
              <div className="bg-twilight h-3 w-1/2 rounded-sm" />
              <div className="bg-twilight h-3 rounded-sm" />
            </div>
          </div>
        ))}
      {results?.waterList.map(({ fishing_water, tag_line }) => {
        const { external_id, name } = fishing_water;
        return (
          <div
            onClick={handleOnWaterClick(fishing_water)}
            onKeyDown={handleOnWaterClick(fishing_water)}
            key={external_id}
            role="button"
            tabIndex={0}
          >
            <div className="hover:bg-twilight/30 flex px-6 py-2 font-normal">
              <div className="flex-none">
                <NextImage alt="Water Icon" width={40} height={40} src={waterIcon} />
              </div>
              <div className="ml-4 w-full place-self-center overflow-hidden">
                <div>{name}</div>
                <div className="truncate text-sm" title={tag_line}>
                  {tag_line}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {locationList.length === 0 && results?.waterList.length === 0 && (
        <Text variant="md" className="mx-4 mb-0 pt-3 pb-4 text-center">
          No results found.
        </Text>
      )}
      {locationList.slice(0, MAX_NUMBER_OF_LOCATIONS_IN_RESULT).map(location => {
        const context = location.context?.map(item => item.name).join(', ');
        const {
          center: [longitude, latitude],
          name,
        } = location.resource;
        return (
          <NonCrawlableLink
            key={`${context}${name}`}
            routerPushTo={getExploreUrl({ latitude, longitude, drawerView: 'catches' })}
            onClickEvent={handleOnLocationClick({ name, latitude, longitude })}
            ariaLabel={name}
          >
            <div className="hover:bg-twilight/30 flex px-6 py-2 font-normal">
              <div className="flex-none">
                <NextImage alt="Water Icon" width={40} height={40} src={locationIcon} />
              </div>
              <div className="ml-4 w-full place-self-center overflow-hidden">
                <div>{name}</div>
                <div className="truncate text-sm" title={context}>
                  {context}
                </div>
              </div>
            </div>
          </NonCrawlableLink>
        );
      })}
    </div>
  );
};
